import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Loader2, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { forgotPassword } from "@/lib/api";
import { AuthShell } from "./AuthShell";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.includes("@") || !email.includes(".")) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPassword({ email });
      if (res.data?.email_delivery_failed) {
        toast.warning("Code generated but email delivery failed. Try again shortly.");
      } else {
        toast.success("If that account exists, we just emailed you a reset code");
      }
      navigate(`/verify?email=${encodeURIComponent(email)}&flow=reset`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="mb-8 flex items-center justify-between">
        <Logo />
        <ThemeToggle />
      </div>

      <div className="mb-8">
        <Link to="/auth" className="mb-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-foreground" data-testid="forgot-back-link">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Forgot password?</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter your email — we'll send you a 6-digit code to reset it.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex w-12 items-center justify-center text-muted-foreground">
            <Mail className="h-4 w-4" />
          </div>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@domain.com"
            required
            autoFocus
            className="h-12 rounded-xl bg-background/60 pl-12 pr-4"
            data-testid="forgot-email-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="h-12 w-full rounded-xl text-[15px] font-medium"
          data-testid="forgot-submit-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send reset code <ArrowRight className="ml-2 h-4 w-4" /></>}
        </Button>
      </form>
    </AuthShell>
  );
}
